// PLIEGO TARIFARIO REAL — la mitad que faltaba de la simbiosis con FluxAI.
//
// FluxAI captura mes a mes el pliego por operador (Captación tarifaria, SQL 225) mientras
// Solar Hub calculaba el ahorro del cliente con una tabla de REFERENCIA nacional.
//
// Es el PRIMER consumidor del espejo (`services/flux.js`): no tiene canal propio ni sabe de
// tokens ni del workflow viejo — pide `pliego` y el espejo resuelve por dónde. El siguiente dato que Solar
// Hub necesite de FluxAI (Factor M, telemetría, entitlements) se escribe igual de corto.
//
// 🔑 REGLA: es un ENRIQUECIMIENTO, nunca un requisito. Si el espejo no responde, si no hay
// pliego para ese operador o si nada está configurado, `obtenerPliego` devuelve null y el
// cotizador sigue con su motor CREG. El usuario no puede quedarse sin cotización porque otra
// plataforma de la suite esté caída.

import { pedirAFlux, reiniciarCanalFlux } from './flux';
import { hubGet } from './hub';

// El pliego cambia una vez al mes: cachear evita un request por cada tecla del formulario.
const cache = new Map();
const TTL_MS = 30 * 60 * 1000;

const llave = (p) => [p.operador || '', p.mercado || 'sin', p.nivel || 1, p.estrato ?? ''].join('|');

/**
 * Trae el pliego vigente del operador. Devuelve `null` cuando no hay dato utilizable —
 * el llamador NO tiene que distinguir "falló" de "no existe": en ambos casos usa su motor.
 *
 * @returns {Promise<null | {componentes, costoUnitario, vigenteDesde, cargos, operador}>}
 */
export async function obtenerPliego({ operador, mercado = 'sin', nivel = 1, estrato = null } = {}) {
  const k = llave({ operador, mercado, nivel, estrato });
  const hit = cache.get(k);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.valor;

  const r = await pedirAFlux(
    'pliego',
    { operador_red: operador || null, mercado, nivel_tension: nivel, estrato },
    null // el espejo no tiene otro escalón
  );

  let valor = null;
  if (r && r.encontrado && r.componentes && r.componentes.generacion != null) {
    valor = {
      componentes: r.componentes,
      costoUnitario: Number(r.costo_unitario) || null,
      // La FECHA es parte del dato: permite decirle al cliente de cuándo es su tarifa
      // en vez de presentarla como si fuera de hoy.
      vigenteDesde: r.vigente_desde || null,
      cargos: r.cargos || null,
      operador: r.operador_red || operador || null,
    };
  }

  // 🔴 SEGUNDA FUENTE, medida el 7-sep-2026. FluxAI **no tiene pliego para ninguno de nuestros
  // operadores** —ENEL, EMCALI y CEELVA responden `encontrado:false · sin pliego root` contra el
  // espejo de producción—, así que el cotizador venía cayendo al catálogo local o al CU plano
  // mientras el hub-api guardaba el pliego real de 7 operadores (SUI + el declarado de CEELVA).
  //
  // 🔑 El ORDEN importa y es este a propósito: primero el pliego que capturó FluxAI para ESE
  // cliente (es el de su operador, mes a mes), y solo si no hay, el que este servidor sincroniza
  // de la fuente pública. Nunca al revés: lo capturado para el cliente es más específico que lo
  // publicado en general.
  if (!valor && operador) {
    try {
      const r = await hubGet('tarifas/pliego', { operador });
      if (r && r.encontrado && r.componentes && r.componentes.generacion != null) {
        valor = {
          componentes: r.componentes,
          costoUnitario: Number(r.costo_unitario) || null,
          vigenteDesde: r.sincronizado_en || null,
          cargos: null,
          operador: r.operador_resuelto || operador,
          // Las tablas publicadas por el OR: con ellas el cotizador muestra el recibo REAL.
          tarifaPublicada: r.tarifa_residencial || null,
          tarifaNoResidencial: r.tarifa_no_residencial || null,
        };
      }
    } catch {
      // Es un ENRIQUECIMIENTO, no un requisito: si el hub-api no contesta, el cotizador sigue con
      // su motor. Nadie se queda sin cotización porque falte una tarifa más precisa.
    }
  }

  cache.set(k, { at: Date.now(), valor });
  return valor;
}

/** Vacía la caché (útil en pruebas y al cambiar de sesión). */
export function limpiarCachePliego() {
  cache.clear();
  // También el recuerdo de "el canal directo no está": si no, una prueba que lo apaga
  // contamina a las siguientes y el estado queda pegado entre sesiones del cliente.
  reiniciarCanalFlux();
}
